import { formatMoney } from '@shopify/theme-currency';

const
    METAFIELD_NAMESPACE = 'abv4',
    LONG_TITLE_THRESHOLD_CHARS = 18,
    COLLECTION_HANDLE_WHITELIST = [
        'kits',
        'enhancers',
        'accessories-and-clothing',
        'tools',
        'oils-and-creams',
    ];

const ajaxIdToStorefrontId = (ajaxId, objectType) =>
    btoa(`gid://shopify/${objectType}/${ajaxId}`);

export const productIdAjaxToStorefront = (ajaxId) =>
    ajaxIdToStorefrontId(ajaxId, 'Product');

export const variantIdAjaxToStorefront = (ajaxId) =>
    ajaxIdToStorefrontId(ajaxId, 'ProductVariant');

export const storefrontIdToAjaxId = (storefrontId) => {
    const [ajaxId] = /\d+$/.exec(atob(storefrontId)) || [];
    return ajaxId ? parseInt(ajaxId, 10) : null;
};

const formatStorefrontAmount = (amount) =>
    formatMoney(parseFloat(amount).toFixed(2), window.moneyFormat);

export class Product {
    constructor(ajax, storefront) {
        this.ajax = ajax || {};
        this.storefront = storefront || {};
    }
    hasColorOption() {
        const { ajax, storefront } = this;
        let result = false;
        if(ajax.options[0].name === "Color") {
            result = true;
        }
        return result;
    }
    activeVariantColorOption(variantIndex) {
        const variant = this.getVariants(variantIndex || 0);
        if(variant[0] === null)
            return null;

        return variant[0].option1 || null;
    }

    getImages(variantIndex) {
        const { ajax, storefront } = this;
        const hasColor = this.hasColorOption(variantIndex || 0);
        const color = this.activeVariantColorOption(variantIndex);
        let images = [];
        for(const image of ajax.media) {
            if(!hasColor) {
                images.push({alt: image.alt, url: image.src})
            } else {
                if(image.alt !== null && image.alt.includes(color)) {
                    images.push({alt: image.alt, url: image.src})  
                }
            }
        }
       return images;
    }

    /* Helper to do a bounds-checked lookup in the two variant arrays. */
    getVariants(variantIndex) {
        const { ajax, storefront } = this;
        let ajaxVariant = null, storefrontVariant = null;
        if (variantIndex !== null) {
            if (storefront.variants) {
                const edge = storefront.variants.edges[variantIndex];
                if (edge !== undefined)
                    storefrontVariant = edge.node;
            }
            if (ajax.variants) {
                const variant = ajax.variants[variantIndex];
                if (variant !== undefined)
                    ajaxVariant = variant;
            }
        }

        return [ajaxVariant, storefrontVariant];
    }

    /* Returns the variant index corresponding to an AJAX API variant ID. */
    findVariantById(id) {
        const { ajax: { variants } } = this;
        if (variants) {
            for (let index = 0; index < variants.length; ++index)
                if (variants[index].id == id)
                    return index;
        }
        return null;
    }

    hasMultipleVariants() {
        const { ajax, storefront } = this;
        return ajax.variants != null && ajax.variants.length > 1 ||
            storefront.variants != null && storefront.variants.length > 1;
    }

    available(variantIndex = null) {
        const [ajaxVariant, storefrontVariant] = this.getVariants(variantIndex);
        if (storefrontVariant !== null)
            return storefrontVariant.availableForSale;
        if (ajaxVariant !== null)
            return ajaxVariant.available;

        const { ajax, storefront } = this;
        return storefront.availableForSale || ajax.available || false;
    }

    ajaxId() {
        const { ajax, storefront } = this;
        if (ajax.id != null)
            return ajax.id;
        if (storefront.id != null)
            return storefrontIdToAjaxId(storefront.id);
        return null;
    }

    storefrontId() {
        const { ajax, storefront } = this;
        if (storefront.id != null)
            return storefront.id;
        if (ajax.id != null)
            return productIdAjaxToStorefront(ajax.id);
        return null;
    }

    handle() {
        const { ajax, storefront } = this;
        return storefront.handle || ajax.handle || null;
    }

    optionName() {
        const { ajax, storefront } = this;
        if(ajax.variants.length === 1)
            return null;

        return 'Select ' + ajax.options[0].name.toLowerCase() + ':' || 'Select ' + storefront.options[0].name.toLowerCase() + ':';
    }

    url() {
        const { ajax, storefront } = this;
        return storefront.onlineStoreUrl || ajax.url || null;
    }

    title() {
        const { ajax, storefront } = this;
        return storefront.title || ajax.title || null;
    }

    hasLongTitle() {
        const title = this.title();
        return title !== null && title.length >= LONG_TITLE_THRESHOLD_CHARS;
    }

    /* Returns a string that can serve as a 'category' for the product in a
     * couple of places. */
    formatCollections() {
        const { ajax, storefront } = this, { collections } = storefront;
        if (collections) {
            for (const { node: { title, handle } } of collections.edges) {
                if (COLLECTION_HANDLE_WHITELIST.indexOf(handle) >= 0)
                    return title;
            }
        }
        return storefront.productType || ajax.type || null;
    }

    formatSaving(variantIndex = null) {
        const { ajax, storefront } = this,
            [va, vsf] = this.getVariants(variantIndex || 0);
        let priceRatio = null;

        if (variantIndex !== null || (storefront.variants && storefront.variants.edges.length === 1)) {
            if (vsf !== null && vsf.price != null) {
                if (vsf.compareAtPrice != null)
                    priceRatio =  vsf.price.amount / vsf.compareAtPrice.amount;
            } else if (va !== null) {
                if (va.compare_at_price > va.price)
                    priceRatio = va.price / va.compare_at_price;
            }
        }

        if (priceRatio === null && ajax.price != null &&
            ajax.compare_at_price > ajax.price) {
            priceRatio = ajax.price / ajax.compare_at_price;
        }

        /* Format the result. */
        if (priceRatio == null || isNaN(priceRatio) || priceRatio >= 1.0)
            return null;

        const discountPercentage = Math.floor(100 - priceRatio * 100);
        
        return `Save ${discountPercentage}%`;
    }

    isDiscounted(variantIndex = null) {
        const { ajax, storefront } = this,
            [va, vsf] = this.getVariants(variantIndex || 0);

        if (variantIndex !== null || (storefront.variants && storefront.variants.edges.length === 1)) {
            if (vsf !== null && vsf.price != null) {
                return vsf.compareAtPrice !== null &&
                    vsf.compareAtPrice.amount > vsf.price.amount;
            } else if (va !== null) {
                return (va.compare_at_price > va.price);
            }
        }

        return ajax.price != null && ajax.compare_at_price > ajax.price;
    }

    formatSpecialPrice(obj, discountAppliedOn) {
        let parts = [];
        parts.push(`<del class="comparison-price">${formatMoney(obj.original_full_price, window.moneyFormat)}</del>`);
        parts.push(`<span class="price">${formatMoney(obj.discounted_full_price, window.moneyFormat)}</span>`);
        // parts.push(`<br/><span class="">Discount applied on ${discountAppliedOn} products</span>`);
        return parts.join(' ');
    }

    /* Builds a general pricing string. */
    formatPricing(variantIndex = null, options = {}) {

        const { ajax, storefront } = this,
            [va, vsf] = this.getVariants(variantIndex || 0);
        let price = null, comparisonPrice = null, priceIsLowerBound = false;
        
        /* When we're using storefront data, we prefer to get a price from the
         * first variant even for single-variant products because only the
         * variants contain comparison prices. */
        if (variantIndex !== null || (storefront.variants && storefront.variants.edges.length === 1)) {
            if (vsf !== null && vsf.price != null) {
                price = formatStorefrontAmount(vsf.price.amount);
                if (vsf.compareAtPrice != null)
                    comparisonPrice = formatStorefrontAmount(vsf.compareAtPrice.amount);
            } else if (va !== null) {
                if (options.quantity) {
                    price = va.price * options.quantity;
                    price = formatMoney(price, window.moneyFormat);
                } else {
                    price = formatMoney(va.price, window.moneyFormat);
                }

                if (va.compare_at_price > va.price) {
                    if(options.quantity) {
                        comparisonPrice = va.compare_at_price * options.quantity;
                        comparisonPrice = formatMoney(comparisonPrice, window.moneyFormat);
                    } else {
                        comparisonPrice = formatMoney(va.compare_at_price, window.moneyFormat);
                    }
                }
            }
        }
        /* Get a price (which may be a bound) from the product itself. */
        if (price === null) {
            if (ajax.price != null) {
                if (options.quantity) {
                    price = ajax.price * options.quantity;
                    price = formatMoney(price, window.moneyFormat);
                } else {
                    price = formatMoney(ajax.price, window.moneyFormat);
                }
                if (ajax.compare_at_price > ajax.price) {
                    if(options.quantity) {
                        comparisonPrice = ajax.compare_at_price * options.quantity;
                        comparisonPrice = formatMoney(comparisonPrice, window.moneyFormat);
                    } else {
                        comparisonPrice = formatMoney(ajax.compare_at_price, window.moneyFormat);
                    }
                }
                priceIsLowerBound = ajax.price_varies;
            } else if (storefront.priceRange != null) {

                const { minVariantPrice, maxVariantPrice } = storefront.priceRange;
                price = formatStorefrontAmount(minVariantPrice.amount);
                priceIsLowerBound = minVariantPrice.amount !== maxVariantPrice.amount;
            }
        }

        /* Assemble the result. */
        if (price === null)
            return null;

        if (!options.comparison || comparisonPrice === price)
            comparisonPrice = null;


        const parts = [];
        if (options.range && priceIsLowerBound)
            parts.push('From');
        if (options.html) {
            const { label, comparisonLabel } = options;
            if (comparisonPrice !== null) {
                // if (comparisonLabel)
                //     parts.push(`<strong class="comparison-label">${comparisonLabel}</strong>`);
                parts.push(`<del class="comparison-price">${comparisonPrice}</del>`);
            }

            if (label)
                parts.push(`<strong class="price-label">${label}</strong>`);
            parts.push(`<span class="price">${price}</span>`);
        } else {
            if (comparisonPrice !== null)
                return `Normally ${comparisonPrice}, now ${price}`;
            parts.push(price);
        }

        return parts.join(' ');
    }

    /* Get a featured image for a product, optionally considering the selected
     * variant. Prefers Storefront API images because we can size those in the
     * request. */
    featuredImage(variantIndex = null, placeholder = null) {
        const { ajax, storefront } = this, [va, vsf] = this.getVariants(variantIndex);

        let result = null;
        if (variantIndex !== null) {
            if (vsf !== null && vsf.image)
                result = vsf.image;
            else if (va !== null && va.featured_image)
                result = { url: va.featured_image };
        }

        if (result === null) {
            const { images } = storefront;
            if (images && images.edges.length !== 0) {
                const [{ node: featured }] = images.edges;
                if (featured)
                    result = featured;
            }
        }

        if (result === null) {
            if (ajax.featured_image)
                result = { url: ajax.featured_image };
            else if (placeholder != null)
                result = { url: placeholder };
        }

        if (result !== null && !result.altText)
            result.altText = this.title();

        return result;
    }

    metafield(key, namespace = METAFIELD_NAMESPACE, variantIndex = null) {
        let storefrontFields = null, ajaxFields = null;
        if (variantIndex !== null) {
            const [, vsf] = this.getVariants(variantIndex);
            if (vsf === null)
                return null;
            storefrontFields = vsf.metafields;
        } else {
            ajaxFields = this.ajax.metafields;
            storefrontFields = this.storefront.metafields;
        }

        let result = null;

        if (storefrontFields) {
            for (const { node: { key: fieldKey, value } } of storefrontFields.edges) {
                if (fieldKey === key) {
                    result = value;
                    break;
                }
            }
        }

        if (result === null && ajaxFields != null && typeof ajaxFields === 'object')
            result = ajaxFields[key];

        if (typeof result !== 'string')
            return null;

        return result.trim() || null;
    }

    description() {
        const { ajax, storefront } = this;
        return storefront.description || ajax.description || '';
    }

    getSellingPlan() {
        const sellingPlan = this.ajax.selling_plan_groups;
        if(sellingPlan.length > 0)
            return sellingPlan[0];
        else
            return null;
    }

    getOptions() {
        const { ajax, storeFront } = this;
        return ajax.options || storeFront.options || null;
    }

    variants() {
        const { ajax, storeFront } = this;

        return ajax.variants || storeFront.variants || null;
    }

    price() {
        const { ajax, storeFront } = this;

        return ajax.price || storeFront.price || null;
    }
}
